import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import AnimatedNumber from "../components/AnimatedNumber";
import CaseStudyHero from "../components/CaseStudyHero";
import { breadcrumbSchema } from "../lib/schemas";

const SafeImage = ({ src, alt, className = "" }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={`relative w-full aspect-[4/3] bg-dodgers/5 border-2 border-dashed border-lime flex items-center justify-center ${className}`}
        role="img"
        aria-label={`${alt} — placeholder`}
      >
        <span className="font-condensed uppercase tracking-widest text-[12px] text-dodgers/60">
          Foto in arrivo
        </span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`w-full h-auto ${className}`}
    />
  );
};

const RelatedCard = ({ to, title, eyebrow }) => (
  <Link
    to={to}
    className="block border border-dodgers/15 p-6 md:p-7 hover:border-lime transition-colors group"
    data-testid={`related-${to.replace(/[^a-z0-9]+/g, "-")}`}
  >
    <p className="font-condensed uppercase tracking-widest text-[12px] text-mandrogni mb-2">
      {eyebrow}
    </p>
    <p className="font-display text-[20px] md:text-[22px] leading-tight group-hover:text-lime transition-colors">
      {title}
    </p>
    <span className="inline-block mt-4 font-condensed uppercase tracking-widest text-[12px] text-dodgers/60">
      Vedi il case →
    </span>
  </Link>
);

const WorkRealeMutuaTogetherForUkraine = () => {
  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Reale Mutua × La Giornata Tipo", url: "/works/reale-mutua-together-for-ukraine" },
  ]);

  return (
    <>
      <Seo
        title="Reale Mutua × La Giornata Tipo: 460K per Ucraina | OnSports Case"
        description="#BTogetherforUkraine: Reale Mutua Basket Torino raccolta fondi per bambini ucraini al Regina Margherita. 460K+ impressions amplificate da La Giornata Tipo, 2022."
        path="/works/reale-mutua-together-for-ukraine"
        image="/images/works/reale-mutua-together-for-ukraine/hero.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/reale-mutua-together-for-ukraine/hero.jpg"
        imageAlt="Reale Mutua Basket Torino × La Giornata Tipo — #BTogetherforUkraine"
        eyebrow="● Case study · Social Impact · Cultural Impact"
        titleLines={[
          "Reale Mutua × La Giornata Tipo.",
          "Un match per",
          "L'UCRAINA.",
        ]}
        highlightWord="L'UCRAINA."
        subtitle="Una maglia che cambia colore, una community che amplifica il messaggio, una raccolta fondi per i bambini ucraini con cancro al Regina Margherita di Torino."
        meta="2022  ·  Cliente — Reale Mutua Basket Torino  ·  Feat. — La Giornata Tipo  ·  Format — #BTogetherforUkraine"
        overlayOpacity={0.45}
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="bg-galactico text-dodgers py-16 md:py-24" data-testid="section-overview">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● Overview</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[24ch]">
              Quando il basket cambia colore, e una community amplifica il messaggio.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-mandrogni">
              <p>
                Marzo. Il conflitto in Ucraina è alle prime settimane, ma la mobilitazione civile in
                Italia è già forte. Reale Mutua Basket Torino sta per disputare un match cruciale
                della Serie A2 maschile: il playoff promozione contro Reggio Emilia. Decide di
                trasformarlo in qualcosa di più grande di una partita — in un gesto pubblico di
                vicinanza all'Ucraina e in una raccolta fondi concreta.
              </p>
              <p>
                Il concept: cambiare la divisa per quella sera. Sostituire il blu Reale Mutua con il
                blu della bandiera ucraina. Aggiungere allo skyline di Torino quello di Kiev,
                riflesso, come segno di gemellaggio simbolico. OnSports ha lavorato
                sull'amplificazione del messaggio coinvolgendo La Giornata Tipo — la community
                italiana di basket più seguita — come content partner, garantendo presenza in arena,
                copertura real-time e reach nazionale.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 3 — IL CONTESTO */}
      <section className="bg-dodgers text-galactico py-20 md:py-28" data-testid="section-contesto">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <p className="font-condensed uppercase tracking-widest text-[13px] text-lime">
              ● Il contesto
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[24ch]">
              Una città che si è sempre dimostrata generosa con chi ne ha più bisogno.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-galactico/85">
              <p>
                Il match contro Reggio Emilia non era una partita qualsiasi. Era il playoff
                promozione, una di quelle serate in cui il palazzetto è pieno, i media regionali
                coprono l'evento, e ogni gesto del club ha un peso comunicativo amplificato. Reale
                Mutua Basket Torino ha scelto di usare quel peso per qualcosa che andasse oltre lo
                sport — un'iniziativa di solidarietà istituzionalizzata sotto il nome
                #BTogetherforUkraine.
              </p>
              <p>
                La raccolta fondi era destinata ai bambini ucraini con cancro accolti al Regina
                Margherita Children's Hospital di Torino — uno dei centri pediatrici di riferimento
                italiani che in quelle settimane aveva iniziato ad accogliere piccoli pazienti in
                fuga dal conflitto. Una causa concreta, locale, immediata. Non un gesto astratto.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 4 — IL CONCEPT CREATIVO */}
      <section className="bg-galactico text-dodgers py-20 md:py-28" data-testid="section-concept">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● Il concept creativo</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6">Una maglia diventa bandiera.</h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-mandrogni">
              <p>
                L'idea del club è stata radicale nella sua semplicità: trasformare la divisa —
                l'elemento più riconoscibile di una squadra, quello con cui giochi davanti a
                migliaia di persone — in un veicolo di messaggio. Il blu Reale Mutua è stato
                sostituito col blu della bandiera ucraina. Il giallo, già presente nel kit storico,
                è diventato il giallo della bandiera. La grafica della maglia ha incorporato lo
                skyline di Torino con quello di Kiev riflesso sotto, come due città che si guardano
                nello stesso specchio d'acqua.
              </p>
              <p>
                A fine primo tempo, durante l'intervallo del match, le maglie sono state donate ad
                alcune "vecchie glorie" del basket torinese, incluso Meo Sacchetti — coach della
                Nazionale Italiana di basket. Il gesto in arena è diventato immediatamente
                contenuto: foto, video, condivisione social. La maglia stessa è poi stata messa in
                vendita online, con il ricavato destinato interamente alla raccolta fondi per il
                Regina Margherita.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 5 — LA DICHIARAZIONE */}
      <section className="bg-dodgers text-galactico py-16 md:py-20" data-testid="section-pullquote">
        <div className="container-os max-w-[880px] mx-auto">
          <RevealOnScroll>
            <p
              className="font-display italic leading-[1.3]"
              style={{ fontSize: "clamp(24px, 3.5vw, 36px)" }}
            >
              «Abbiamo deciso di metterci la faccia, cambiando l'elemento che ci distingue su tutti
              i campi d'Italia per una partita: la nostra divisa. Abbiamo cambiato i colori,
              sostituendo il classico blu con il blu della bandiera ucraina; il giallo c'era già,
              come a suggerire un matrimonio che doveva farsi. Abbiamo aggiunto allo skyline di
              Torino quello di Kiev, riflesso, per simboleggiare la vicinanza della nostra città —
              da sempre generosa con chi ne ha più bisogno — alla capitale.»
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 text-center font-condensed uppercase tracking-widest text-[13px] text-lime">
              Reale Mutua Basket Torino
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 6 — IMMAGINE FULL-WIDTH */}
      <section className="bg-galactico text-dodgers py-12 md:py-16" data-testid="section-gallery">
        <div className="container-os">
          <div className="max-w-[1200px] mx-auto">
            <SafeImage
              src="/images/works/reale-mutua-together-for-ukraine/gallery-01.jpg"
              alt="La maglia BTogetherforUkraine consegnata a Meo Sacchetti e alle vecchie glorie del basket torinese"
            />
            <p className="mt-5 text-center text-[14px] italic text-mandrogni">
              La maglia del match: blu Ucraina, giallo Torino, skyline gemellati — consegnata in
              arena alle vecchie glorie del basket torinese, incluso Meo Sacchetti.
            </p>
          </div>
        </div>
      </section>

      {/* SEZ. 7 — L'APPROCCIO ONSPORTS */}
      <section className="bg-galactico text-dodgers py-20 md:py-28" data-testid="section-approccio">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● L'approccio OnSports</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[24ch]">
              Una community giusta vale 460mila impressions.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-mandrogni">
              <p>
                Il concept della maglia era già potente. Ma in una città media italiana, una serata
                di Serie A2 — anche se importante — rischiava di esaurire la sua eco nell'arco di
                poche ore. Per OnSports la sfida era amplificare il messaggio oltre i confini di
                Torino, mantenendo l'autenticità del gesto: nessuna campagna paid invasiva, nessuna
                pubblicità travestita. Solo amplificazione community-driven.
              </p>
              <p>
                La scelta è ricaduta su La Giornata Tipo — la community italiana dedicata al basket
                con più follower e più engagement organico nel panorama digitale italiano. Una
                community che parla la lingua dei fan reali della pallacanestro, non il linguaggio
                della comunicazione aziendale. Coinvolgerli non significava "pagare un'inserzione".
                Significava trovare il messaggero giusto per la storia giusta.
              </p>
              <p>
                La Giornata Tipo è stata presente all'evento, ha pubblicato contenuti real-time, ha
                raccontato l'iniziativa con la sua voce. Il risultato è stato un volume di
                impressions e click reali, organici, generati dalla community basket italiana — quel
                pubblico che, se attivato bene, mobilita davvero le sue reti.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 8 — L'IMPATTO */}
      <section className="bg-dodgers text-galactico py-24 md:py-32" data-testid="section-impatto">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto text-center">
            <RevealOnScroll>
              <p className="font-condensed uppercase tracking-widest text-[13px] text-lime">
                ● L'impatto
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2 className="display-h2 mt-6 max-w-[22ch] mx-auto">
                Numeri al servizio dei <span className="text-lime">BAMBINI</span>.
              </h2>
            </RevealOnScroll>
          </div>

          {/* KPI animati */}
          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-10 max-w-[1100px] mx-auto">
            <div className="text-center min-w-0" data-testid="kpi-impressions">
              <p
                className="font-display text-lime leading-none whitespace-nowrap"
                style={{ fontSize: "clamp(72px, 10vw, 140px)" }}
              >
                <AnimatedNumber end={460} suffix="K+" />
              </p>
              <p className="mt-6 font-condensed uppercase tracking-widest text-[14px] text-galactico/85">
                Impressions cumulate
              </p>
            </div>
            <div className="text-center min-w-0" data-testid="kpi-click">
              <p
                className="font-display text-lime leading-none whitespace-nowrap"
                style={{ fontSize: "clamp(72px, 10vw, 140px)" }}
              >
                <AnimatedNumber end={2} suffix="K+" />
              </p>
              <p className="mt-6 font-condensed uppercase tracking-widest text-[14px] text-galactico/85">
                Click sul link di acquisto
              </p>
            </div>
          </div>

          {/* Destinazione fondi */}
          <RevealOnScroll delay={0.15}>
            <div className="mt-20 max-w-[760px] mx-auto text-center">
              <p className="text-[17px] leading-[1.7] text-galactico/85">
                Il ricavato delle maglie vendute online è stato interamente destinato al Regina
                Margherita Children's Hospital di Torino, per il supporto ai bambini ucraini con
                cancro accolti dall'ospedale in quelle settimane di emergenza. Un'iniziativa locale
                che ha trovato la sua scala nazionale grazie alla mobilitazione della community
                basket italiana.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — CREDITS */}
      <section className="bg-galactico text-dodgers py-16 md:py-24" data-testid="section-credits">
        <div className="container-os max-w-[720px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="font-display leading-tight mt-6" style={{ fontSize: "clamp(28px, 3.5vw, 36px)" }}>
              Chi ha fatto cosa.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <dl className="mt-10 divide-y divide-dodgers/15">
              {[
                ["Concept divisa & iniziativa", "Reale Mutua Basket Torino"],
                ["Community Amplification", "OnSports + La Giornata Tipo"],
                ["Content production real-time", "La Giornata Tipo"],
                ["Cliente", "Reale Mutua Basket Torino"],
                ["Destinazione fondi", "Regina Margherita Children's Hospital — Torino"],
                ["Anno", "2022"],
                ["Format", "#BTogetherforUkraine"],
              ].map(([label, value]) => (
                <div key={label} className="py-4 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-2 md:gap-6">
                  <dt className="font-condensed uppercase tracking-widest text-[13px] text-mandrogni">
                    {label}
                  </dt>
                  <dd className="text-[16px]">{value}</dd>
                </div>
              ))}
            </dl>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-32 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os max-w-[760px]">
          <RevealOnScroll>
            <h2 className="font-display leading-tight" style={{ fontSize: "clamp(28px, 3.5vw, 36px)" }}>
              Hai una causa da raccontare al grande pubblico? Costruiamo la storia{" "}
              <span className="text-lime">INSIEME</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.08}>
            <p className="mt-6 text-[16px] md:text-[17px] leading-[1.7] text-galactico/85 max-w-[600px]">
              Una call iniziale di 30 minuti per capire se il tuo messaggio ha la forza per
              diventare attivazione sociale concreta.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.16}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-social-impact">
                <Button variant="secondary-light">Vedi altri case Social Impact</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 11 — NAVIGATION */}
      <section className="bg-galactico text-dodgers py-16 md:py-20" data-testid="section-related">
        <div className="container-os">
          <RevealOnScroll>
            <p className="font-condensed uppercase tracking-widest text-[13px] text-mandrogni mb-8">
              ● Altri case correlati
            </p>
          </RevealOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            <RelatedCard
              to="/works/scuola-attiva"
              eyebrow="Social Impact · Content"
              title="Scuola Attiva"
            />
            <RelatedCard
              to="/works/credem-extra-ordinary"
              eyebrow="Social Impact · Content & IP"
              title="Credem × Extra Ordinary"
            />
            <RelatedCard
              to="/works/volleyball-world-italia"
              eyebrow="Cultural Impact · Social & Content"
              title="Volleyball World Italia"
            />
          </div>
          <div className="mt-10">
            <Link
              to="/works"
              className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-[13px] text-dodgers hover:text-lime transition-colors"
              data-testid="back-to-works"
            >
              <ArrowLeft size={16} /> Tutti i casi studio
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkRealeMutuaTogetherForUkraine;
